import { isDailyRashifalHubPath } from "../../../edge-db/shared/rashifal-freshness";
import { canonicalItemUrl } from "../../../shared/canonical-url";
import { APP_AVAILABLE } from "../app-availability";
import {
  CONTENT_REVIEWER_ID,
  activeContentReviewer,
  boundReviewEvidence,
  type ReviewEvidence,
} from "../content-reviewer";
import { PAGE_FRESHNESS } from "../page-freshness.generated";
import { freshnessKey } from "../../../edge-db/shared/page-freshness-key";
import { SITE_LOGO, SITE_NAME, SITE_URL } from "./site";

// App store anchors are entity-graph SAME_AS links (rule 26) — only advertise
// them when the app is publicly installable, else they 404 and weaken trust.
const APP_STORE_SAME_AS = [
  "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  "https://apps.apple.com/app/id6743459479",
];

// 2026-07-27: the Wikidata anchor was REMOVED — Q139596352 was deleted on
// 2026-07-20 by an admin via Requests for Deletions ("Does not meet the
// notability policy"). Rule 26 forbids a SAME_AS that does not resolve: a
// crawler follows it, so a dead anchor is worse than an absent one. Re-add only
// once an entity exists again AND `sync_wikidata_to_schema.py` exits 0 — and
// per rule 26, sequence recreation AFTER press coverage or it fails WD:N again.
//
// Replaced by two owner-confirmed, resolving anchors (LinkedIn + Pinterest), so
// the entity graph is honestly above the G100 >=5 threshold rather than propped
// up by a 404. NOTE: an `x.com/ishvaram` account exists and is titled
// "Ishvaram" but is NOT ours (owner-confirmed 2026-07-27) — never add it. That
// is why an anchor needs owner confirmation, not just an HTTP 200.
export const SAME_AS: string[] = [
  ...(APP_AVAILABLE ? APP_STORE_SAME_AS : []),
  "https://ishvaram.com",
  "https://www.instagram.com/ishvaram.in/",
  "https://www.facebook.com/ishvaram.in",
  "https://www.youtube.com/channel/UCmLGFR2IpfqgoWaHuD-L9Vg",
  "https://www.linkedin.com/company/ishvaram/",
  "https://www.pinterest.com/ishvaramindia/",
  // Owner-confirmed 2026-07-30 (owner set the org avatar; we operate the org):
  // public org profile with README, populated the same day (rule 26).
  "https://github.com/Ishvaram-Business",
  // Owner-created + confirmed 2026-07-30. NOTE for the liveness guard: Crunchbase
  // 403s datacenter probes (bot wall) — that is the documented INCONCLUSIVE
  // class, never a dead-anchor alarm (rule 26).
  "https://www.crunchbase.com/organization/ishvaram",
  // Google Business Profile CID link (owner-confirmed 2026-07-30: derived from
  // the owner's live Maps place URL, KG mid /g/11xf9bmq8p; the dashboard
  // "business id" is a DIFFERENT namespace — never anchor that). Maps serves a
  // JS shell to datacenter probes: liveness-guard INCONCLUSIVE class (rule 26).
  "https://maps.google.com/?cid=10448390172209870661",
];

/** Stable @id for the founder Person node — the Article `author` on every page. */
export const FOUNDER_ID = `${SITE_URL}#founder`;

/**
 * The real founder (owner-confirmed 2026-07-23), who builds the tools and
 * writes the guides — the human `author` behind every Next-rendered Article.
 * Every field is a fact already public on-site: the name and founder role from
 * this Organization node, "operated by Digesh Nanda" on /terms/ and /contact/,
 * and /about/ as the on-site profile. Deliberately NO `sameAs` (the Organization
 * anchors are company profiles, not a personal one), NO `jobTitle` (/about/
 * states none, and `Organization.founder` already encodes the role), NO
 * `knowsAbout` and NO astrology credential: the credentialed Jyotish reviewer
 * is a different, real person (`content-reviewer.ts`), and the byline must
 * never blur the two (ADR-0022 honesty contract). Emitted as a FULL node wherever it is used so it
 * defines `#founder` on the page itself and the entity graph stays resolvable
 * (rule 26) even where the layout graph is absent.
 */
export const FOUNDER = {
  "@type": "Person",
  "@id": FOUNDER_ID,
  name: "Digesh Nanda",
  url: `${SITE_URL}/about/`,
} as const;

export const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}#org`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: SITE_LOGO,
  },
  // E-E-A-T: the entity's demonstrated expertise domain + its editorial/methodology
  // page (how facts are computed, what the AI never does). Honest, org-level — no
  // fabricated human (content-reviewer.ts stays owner-gated).
  knowsAbout: [
    "Vedic astrology",
    "Jyotish",
    "Kundali",
    "Panchang",
    "Muhurat",
    "Kundli Matching",
    "Dosha analysis",
    "Nakshatra",
    "Vimshottari Dasha",
    "Sade Sati",
    "Kaal Sarp Dosha",
    "Government Job Yoga",
    "Love Marriage Astrology",
    "Ashtakvarga",
    "Shodashvarga Divisional Charts",
    "Swiss Ephemeris Calculations",
  ],
  publishingPrinciples: `${SITE_URL}/about/`,
  // E-E-A-T contact signal (YMYL): a reachable support channel is a documented
  // quality-rater expectation. Email only — the same address already public on
  // /contact/; no fabricated NAP.
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@ishvaram.com",
    availableLanguage: ["en", "hi"],
  },
  // One public, business-wide policy for merchant listings. This mirrors the
  // visible /refund-policy/ terms; reason-specific fees distinguish a normal
  // change-of-mind return from a damaged or incorrect delivery.
  hasMerchantReturnPolicy: {
    "@type": "MerchantReturnPolicy",
    merchantReturnLink: `${SITE_URL}/refund-policy/`,
    applicableCountry: "IN",
    returnPolicyCountry: "IN",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 7,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    customerRemorseReturnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    itemDefectReturnFees: "https://schema.org/FreeReturn",
    refundType: "https://schema.org/FullRefund",
  },
  // Real founder + founding year (owner-confirmed 2026-07-23). The same node
  // is every Article's `author`, so founder and author are one entity.
  founder: FOUNDER,
  foundingDate: "2025",
  ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
    description:
      "Vedic astrology tools powered by Swiss Ephemeris — free kundali, rashifal, dosha analysis, muhurat, and AI-guided Jyotish consultations.",
    areaServed: "IN",
    knowsLanguage: ["en", "hi", "sa", "ta", "te", "mr", "bn", "gu", "kn", "ml"],
  };
}

export interface BreadcrumbEntry {
  name: string;
  url: string;
}

/** BreadcrumbList with every item normalized through the canonical URL owner. */
export function breadcrumbSchema(trail: BreadcrumbEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((breadcrumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: breadcrumb.name,
      item: canonicalItemUrl(
        breadcrumb.url.startsWith("http") ? breadcrumb.url : `${SITE_URL}${breadcrumb.url}`
      ),
    })),
  };
}

export function buildNestedBreadcrumbs(segments: Array<{ name: string; url: string }>) {
  return breadcrumbSchema(segments);
}

export const CORE_PILLAR_HUBS: Array<{ name: string; path: string }> = [
  { name: "Free Janam Kundali", path: "/free-kundali/" },
  { name: "Kundli Matching & Milan", path: "/kundli-matching/" },
  { name: "Today Panchang & Tithi", path: "/panchang/" },
  { name: "Choghadiya Muhurat", path: "/choghadiya/" },
  { name: "Aaj Ka Rashifal", path: "/aaj-ka-rashifal/" },
  { name: "Shubh Muhurat Timings", path: "/shubh-muhurat/" },
];

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    alternateName: "Ishvaram AI Vedic Astrology",
    publisher: { "@id": `${SITE_URL}#org` },
    inLanguage: ["en", "hi", "sa", "ta", "te", "mr", "bn", "gu", "kn", "ml"],
    hasPart: CORE_PILLAR_HUBS.map((hub) => ({
      "@type": "WebPage",
      "@id": `${SITE_URL}${hub.path}`,
      url: `${SITE_URL}${hub.path}`,
      name: hub.name,
      isPartOf: { "@id": `${SITE_URL}#website` },
    })),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export interface CollectionPageInput {
  name: string;
  description: string;
  url: string;
  inLanguage?: "en" | "hi";
}

/** CollectionPage schema — for directories/catalogs, never a single-person profile. */
export function collectionPageSchema(input: CollectionPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: canonicalItemUrl(input.url),
    inLanguage: input.inLanguage ?? "en",
    isPartOf: { "@id": `${SITE_URL}#website` },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  inLanguage?: "en" | "hi";
  speakableSelectors?: string[];
  about?: Array<Record<string, unknown>>;
  /**
   * Completed page-level human-review evidence. Never set this from the global
   * reviewer profile alone; the values must match a published ledger entry
   * (`boundReviewEvidence` in content-reviewer.ts is the one predicate).
   */
  reviewEvidence?: ReviewEvidence;
}

/**
 * Same-day surfaces: their `dateModified` is the ENGINE's IST civil date for
 * today's content (rule 64 same-day contract), which the copy-freshness
 * registry must never replace — `/rashifal/` is registered for its evergreen
 * copy, yet its live gate asserts `"dateModified":"<today>"` daily. The two
 * rashifal hubs come from the one same-day owner the live guard, exporter and
 * edge worker share; the rest are the daily engine routes that pass today's
 * date the same way.
 */
const SAME_DAY_ROUTE_PATHS: ReadonlySet<string> = new Set([
  "/daily-horoscope/",
  "/today-horoscope/",
  "/tomorrow-horoscope/",
  "/panchang/today/",
  "/choghadiya/today/",
  "/rahu-kaal/today/",
]);

function isSameDaySurface(pathname: string): boolean {
  return isDailyRashifalHubPath(pathname) || SAME_DAY_ROUTE_PATHS.has(pathname);
}

/**
 * Rule 48 date truth: when the route is registered in the git-derived
 * `PAGE_FRESHNESS` projection, that evidence date IS the Article
 * `dateModified` — a caller-supplied literal can never override it, and a
 * registered page's schema date and visible note cannot drift. Unregistered
 * routes, and same-day surfaces whose date is the engine's, fall back to
 * whatever the caller supplied (or nothing).
 */
export function freshnessDateModified(url: string | undefined): string | undefined {
  if (!url?.startsWith(SITE_URL)) return undefined;
  const route = url.slice(SITE_URL.length).replace(/\/+$/, "") || "/";
  if (isSameDaySurface(`${route}/`)) return undefined;
  // Shared resolver: dynamic families (festival, muhurat) read their family
  // key, so a detail page and its sitemap row cannot disagree (rule 48).
  return (PAGE_FRESHNESS as Record<string, string>)[freshnessKey(route)];
}

/** Evidence dates only: publication as supplied, modification from the freshness owner. */
function articleDates(input: ArticleInput) {
  const dateModified = freshnessDateModified(input.url) ?? input.dateModified;
  return {
    ...(input.datePublished && { datePublished: input.datePublished }),
    ...(dateModified && { dateModified }),
  };
}

/** The page-level review claim — present only for a ledger-bound record. */
function articleReviewClaim(input: ArticleInput) {
  const boundReview = activeContentReviewer() ? boundReviewEvidence(input.reviewEvidence) : null;
  if (!boundReview) return {};
  return {
    reviewedBy: { "@id": CONTENT_REVIEWER_ID },
    dateReviewed: boundReview.reviewedAt.slice(0, 10),
  };
}

/** Article schema — for explanatory / editorial pages. */
export function articleSchema(input: ArticleInput) {
  const url = input.url ? canonicalItemUrl(input.url) : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    ...(url && { url, mainEntityOfPage: url }),
    ...(input.image && { image: input.image }),
    ...articleDates(input),
    inLanguage: input.inLanguage ?? "en",
    author: FOUNDER,
    ...(input.about && { about: input.about }),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: input.speakableSelectors ?? ["[data-answer-summary]", "h1"],
    },
    ...articleReviewClaim(input),
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ChartImageObjectInput {
  title: string;
  imageUrl: string;
  caption?: string;
  width?: number;
  height?: number;
  representativeOfPage?: boolean;
}

/** Schema.org ImageObject for visual charts, Kundali diagrams, and Google Images rich results. */
export function chartImageObjectSchema(input: ChartImageObjectInput) {
  const fullUrl = input.imageUrl.startsWith("http")
    ? input.imageUrl
    : `${SITE_URL}${input.imageUrl.startsWith("/") ? "" : "/"}${input.imageUrl}`;
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: fullUrl,
    url: fullUrl,
    name: input.title,
    ...(input.caption && { caption: input.caption }),
    ...(input.width && { width: input.width }),
    ...(input.height && { height: input.height }),
    ...(input.representativeOfPage !== undefined && {
      representativeOfPage: input.representativeOfPage,
    }),
  };
}

export interface ToolWebApplicationInput {
  name: string;
  description: string;
  url: string;
  applicationCategory?: string;
  operatingSystem?: string;
  isFree?: boolean;
}

/** Schema.org WebApplication with CalculateAction for interactive Vedic calculators. */
export function toolWebApplicationSchema(input: ToolWebApplicationInput) {
  const fullUrl = input.url.startsWith("http")
    ? input.url
    : `${SITE_URL}${input.url.startsWith("/") ? "" : "/"}${input.url}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: input.name,
    description: input.description,
    url: fullUrl,
    applicationCategory: input.applicationCategory ?? "AstrologyApplication",
    operatingSystem: input.operatingSystem ?? "All",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    isPartOf: { "@id": `${SITE_URL}#website` },
    potentialAction: {
      "@type": "CalculateAction",
      name: `Calculate ${input.name}`,
      target: fullUrl,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
    },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

const SCHEMA_TEXT_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  hellip: "...",
  laquo: '"',
  ldquo: '"',
  lsaquo: "'",
  lsquo: "'",
  lt: "<",
  mdash: "-",
  ndash: "-",
  nbsp: " ",
  quot: '"',
  raquo: '"',
  rdquo: '"',
  rsaquo: "'",
  rsquo: "'",
};

function decodeSchemaTextEntities(value: string): string {
  return value.replace(/&(#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity, rawName: string) => {
    const name = rawName.toLowerCase();
    if (name.startsWith("#x")) {
      const codePoint = Number.parseInt(name.slice(2), 16);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    if (name.startsWith("#")) {
      const codePoint = Number.parseInt(name.slice(1), 10);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    return SCHEMA_TEXT_ENTITIES[name] ?? entity;
  });
}

function plainSchemaText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = decodeSchemaTextEntities(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : null;
}

/**
 * FAQPage schema — the single highest-ROI AEO primitive.
 *
 * Rules:
 * - Q and A must be plain text (strip HTML from answer before passing in).
 * - Each answer should be a self-contained, quotable chunk (2–4 sentences).
 * - Every Q&A should contain the page's primary keyword variant so FAQ
 *   additions don't dilute topical focus.
 */
export function faqPageSchema(faqs: FAQItem[]) {
  const seenQuestions = new Set<string>();
  const mainEntity = faqs.flatMap((f) => {
    const question = plainSchemaText(f.question);
    const answer = plainSchemaText(f.answer);
    if (!question || !answer || seenQuestions.has(question)) return [];
    seenQuestions.add(question);
    return [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      },
    ];
  });

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToInput {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration, e.g. "PT2M"
  tool?: string[];
}

/** HowTo schema — for calculator pages ("How to check your Mangal Dosha"). */
export function howToSchema(input: HowToInput) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    ...(input.totalTime && { totalTime: input.totalTime }),
    ...(input.tool && {
      tool: input.tool.map((t) => ({ "@type": "HowToTool", name: t })),
    }),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url && { url: canonicalItemUrl(s.url) }),
    })),
  };
}

/**
 * AEO & Entity Authority Schema.
 *
 * Describes the same publisher entity as the site-wide Organization graph.
 */
export function aeoEntityAuthoritySchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}#org`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    sameAs: SAME_AS,
    publishingPrinciples: `${SITE_URL}/about/`,
    knowsAbout: [
      "Vedic Astrology",
      "Brihat Parashara Hora Shastra",
      "Swiss Ephemeris Astronomy",
      "Panchang Calculations",
      "Kundli Matching & Gun Milan",
      "Vimshottari Dasha Systems",
    ],
  };
}
